import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { useSurveyI18n, type SurveyStrings } from '../i18n';
import type { SurveyDay } from '../report';
import { seasonOfDay, type Season } from '../seasons';
import { fmtCompact, fmtDay, fmtInt } from '../utils';

/*
 * Daily unique visitors for the days of the picked scope, one bar a day in its
 * season's colour (the seasons switch at midnight, so a day is in exactly
 * one). (The payload also carries surveys submitted per day; the client asked
 * for visitors only, 2026-10-07 — add a series here to bring them back.)
 *
 * The date axis is written for the client, in two rows: the day of the month
 * under every bar (with the month name on the first day shown and on the 1st),
 * and the week underneath, numbered inside its season ("S2 · Week 1") — the
 * weeks every other figure on the board is counted in, with a rule between
 * weeks and a heavier one where the next season starts. When the bars get too
 * narrow for a number each, only each week's first day keeps its date.
 *
 * The latest day is a direct label, every bar has a tooltip, the arrow keys
 * read the days, and the table is one click away.
 */

const FALLBACK_COLOR = '#065A8E';
const H = 280;
const PAD = { l: 56, r: 20, t: 16, b: 52 };

/** Axis on round steps (1 / 2 / 5 × 10ⁿ, never below 1 — these are counts). */
function scale(max: number, target = 4): { max: number; step: number } {
  if (max <= 0) return { max: 1, step: 1 };
  const raw = max / target;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const step = Math.max(1, [1, 2, 5, 10].map((m) => m * pow).find((s) => s >= raw) ?? 10 * pow);
  return { max: Math.ceil(max / step) * step, step };
}

/** A column from the baseline up to `top`: 4px rounded data-end, square at the baseline. Null when it has no height. */
function barPath(x: number, top: number, w: number, base: number): string | null {
  const h = base - top;
  if (h <= 0) return null;
  const r = Math.min(4, w / 2, h);
  return `M${x},${base}V${top + r}Q${x},${top} ${x + r},${top}H${x + w - r}Q${x + w},${top} ${x + w},${top + r}V${base}Z`;
}

const latestText = (d: SurveyDay, t: SurveyStrings) => t.dailyLatest(fmtDay(d.date, t.locale, 'long'), fmtInt(d.uniqueVisitors));

type Props = {
  /** The days of the scope, in order. */
  days: SurveyDay[];
  /** The seasons of the scope. */
  seasons: Season[];
};

export function DailyChart({ days, seasons }: Props) {
  const { t } = useSurveyI18n();
  const measured = days.filter((d) => d.uniqueVisitors != null);
  const latest = measured[measured.length - 1];
  const seasonName = (date: string) => {
    const s = seasonOfDay(seasons, date);
    return s ? t.season(s.n) : '—';
  };

  return (
    <section className="su-card su-daily">
      <div className="su-daily-head">
        <h2>{t.dailyTitle}</h2>
        {latest && <span className="su-note">{latestText(latest, t)}</span>}
      </div>

      {measured.length === 0 ? (
        <p className="su-note">{t.dailyEmpty}</p>
      ) : (
        <>
          {seasons.length > 1 && (
            <div className="su-legend">
              {seasons.map((s) => (
                <span key={s.id}>
                  <span className="su-swatch" style={{ background: s.color }} />
                  {t.season(s.n)}
                </span>
              ))}
            </div>
          )}
          <BarChart days={days} seasons={seasons} />
          <p className="su-note">{t.dailyNote}</p>
          <details className="su-daily-table">
            <summary>{t.dailyShowTable}</summary>
            <div className="su-scroll">
              <table className="su-table" aria-label={t.dailyTitle}>
                <thead>
                  <tr>
                    <th>{t.day}</th>
                    <th>{t.seasonCol}</th>
                    <th>{t.uniqueVisitors}</th>
                  </tr>
                </thead>
                <tbody>
                  {days.map((d) => (
                    <tr key={d.date}>
                      <td>{fmtDay(d.date, t.locale, 'short')}</td>
                      <td>{seasonName(d.date)}</td>
                      <td>{fmtInt(d.uniqueVisitors)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        </>
      )}
    </section>
  );
}

function BarChart({ days, seasons }: Props) {
  const { t } = useSurveyI18n();
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(800);
  const [hover, setHover] = useState<number | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const obs = new ResizeObserver(([e]) => setWidth(Math.max(280, e.contentRect.width)));
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const n = Math.max(1, days.length);
  const innerW = width - PAD.l - PAD.r;
  const innerH = H - PAD.t - PAD.b;
  const base = PAD.t + innerH;
  const { max, step } = scale(Math.max(0, ...days.map((d) => d.uniqueVisitors ?? 0)));
  const y = (v: number) => base - (v / max) * innerH;
  const ticks = Array.from({ length: Math.round(max / step) + 1 }, (_v, i) => i * step);
  const lastIdx = days.reduce((a, d, i) => (d.uniqueVisitors != null ? i : a), -1);

  // One band per day; the bar is capped at 24px and never fills its band.
  const band = innerW / n;
  const left = (i: number) => PAD.l + band * i;
  const cx = (i: number) => left(i) + band / 2;
  const barW = Math.max(2, Math.min(24, band * 0.6));

  // The weeks of the scope as runs of day indexes, numbered inside their season.
  const both = seasons.length > 1;
  const weekRuns = seasons.flatMap((s) =>
    s.weeks
      .map((w, k) => {
        const idx = days.flatMap((d, i) => (d.date >= w.start && d.date <= w.end ? [i] : []));
        if (idx.length === 0) return null;
        const full = both ? t.seasonWeek(s.n, k + 1) : t.week(k + 1);
        const short = both ? `${t.seasonShort(s.n)} · ${t.weekShort(k + 1)}` : t.weekShort(k + 1);
        return { week: w.week, season: s.id, from: idx[0], to: idx[idx.length - 1], full, short };
      })
      .filter((r): r is NonNullable<typeof r> => r != null),
  );
  const weekStarts = new Set(weekRuns.map((r) => r.from));
  const colorOf = (date: string) => seasonOfDay(seasons, date)?.color ?? FALLBACK_COLOR;

  // Day labels: every day when each bar has room for its number (≈22px),
  // otherwise each week's first day only. The month is written out on the
  // first day shown and on the 1st, so "6 Oct, 7, 8 … 1 Nov, 2" reads as dates.
  const everyDay = band >= 22;
  const dayLabel = (d: SurveyDay, i: number): string | null => {
    if (!everyDay && !weekStarts.has(i)) return null;
    const dom = Number(d.date.slice(8, 10));
    return i === 0 || dom === 1 || !everyDay ? fmtDay(d.date, t.locale, 'dayMonth') : String(dom);
  };

  const nearest = (clientX: number) => {
    const rect = ref.current?.getBoundingClientRect();
    const i = Math.floor((clientX - (rect?.left ?? 0) - PAD.l) / band);
    return Math.min(lastIdx, Math.max(0, i));
  };

  function onKey(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    setHover((h) => Math.min(lastIdx, Math.max(0, (h ?? lastIdx) + (e.key === 'ArrowRight' ? 1 : -1))));
  }

  const active = hover != null ? days[hover] : null;
  const activeSeason = active ? seasonOfDay(seasons, active.date) : undefined;
  const tipX = hover != null ? left(hover) : 0;
  const flip = tipX + band + 230 > width;
  // Stepping through the days with the arrow keys is a slider: a screen reader
  // announces each day's figure as the value moves.
  const current = days[hover ?? lastIdx];

  return (
    <div
      ref={ref}
      className="su-bar-chart"
      role="slider"
      aria-label={t.dailyChartLabel}
      aria-valuemin={0}
      aria-valuemax={Math.max(0, lastIdx)}
      aria-valuenow={Math.max(0, hover ?? lastIdx)}
      aria-valuetext={current ? latestText(current, t) : undefined}
      tabIndex={0}
      onKeyDown={onKey}
      onFocus={() => setHover((h) => h ?? lastIdx)}
      onBlur={() => setHover(null)}
      onPointerMove={(e: PointerEvent<HTMLDivElement>) => setHover(nearest(e.clientX))}
      onPointerLeave={() => setHover(null)}
    >
      <svg width={width} height={H} aria-hidden="true">
        {active && <rect x={left(hover ?? 0)} y={PAD.t} width={band} height={innerH} className="su-hover-band" />}
        {ticks.map((v) => (
          <g key={v}>
            <line x1={PAD.l} x2={width - PAD.r} y1={y(v)} y2={y(v)} className="su-grid" />
            <text x={PAD.l - 10} y={y(v)} className="su-axis-text" textAnchor="end" dominantBaseline="middle">
              {v === 0 ? '0' : fmtCompact(v)}
            </text>
          </g>
        ))}

        {/* Row 1: the day of the month — centred under its bar, or, when only
            each week's first day is labelled, starting just after the week rule
            so a label wider than its bar never runs across it. */}
        {days.map((d, i) => {
          const text = dayLabel(d, i);
          if (!text) return null;
          return (
            <text key={d.date} x={everyDay ? cx(i) : left(i) + 4} y={base + 18} className="su-axis-text" textAnchor={everyDay ? 'middle' : 'start'}>
              {text}
            </text>
          );
        })}

        {/* Row 2: the campaign week, with a rule where the next one starts. */}
        {weekRuns.map((r, k) => {
          const x0 = left(r.from);
          const x1 = left(r.to) + band;
          const newSeason = k > 0 && weekRuns[k - 1].season !== r.season;
          const text = x1 - x0 >= (both ? 96 : 72) ? r.full : r.short;
          return (
            <g key={r.week}>
              {newSeason && <line x1={x0} x2={x0} y1={PAD.t} y2={base + 44} className="su-season-rule" />}
              {k > 0 && !newSeason && <line x1={x0} x2={x0} y1={base} y2={base + 44} className="su-week-rule" />}
              <text x={(x0 + x1) / 2} y={base + 40} className="su-axis-week" textAnchor="middle">
                {text}
              </text>
            </g>
          );
        })}

        {days.map((d, i) => {
          const shape = d.uniqueVisitors == null ? null : barPath(cx(i) - barW / 2, y(d.uniqueVisitors), barW, base);
          return shape ? <path key={d.date} d={shape} fill={colorOf(d.date)} /> : null;
        })}
      </svg>
      {active && (
        <div className={`su-chart-tip${flip ? ' flip' : ''}`} style={{ left: flip ? tipX : tipX + band, top: PAD.t }}>
          <span className="su-chart-tip-day">{fmtDay(active.date, t.locale, 'long')}</span>
          {both && activeSeason && <span>{t.season(activeSeason.n)}</span>}
          <span className="su-chart-tip-row">
            <strong>{fmtInt(active.uniqueVisitors)}</strong>
            <span>{t.uniqueVisitors}</span>
          </span>
        </div>
      )}
    </div>
  );
}
